/**
 * UserRow
 *
 * Rich table row for admin user management with computed badges and
 * time-based indicators. Delegates actions via props callbacks.
 */
import React from 'react';
import { Building2, CreditCard, Trash2, RefreshCw } from 'lucide-react';
import Tooltip from '../../ui/overlays/Tooltip';
import { 
  formatNumber, 
  formatTimeRemaining, 
  getDayOfWeekInCST, 
  formatDateTimeInCST,
  getUserDisplayName,
  getNextDecisionDate,
  formatPhoneDisplay,
  getSubscriptionBadgeInfo
} from './userRowUtils';
import {
  NameColumn,
  EnterpriseColumn,
  PhoneColumn,
  TrialEndColumn,
  NextDecisionDateColumn,
  MemberSinceColumn,
  LastActiveColumn,
  SubscriptionColumn,
  MetricBadgeColumn,
  EngagementColumn,
  RevenueColumn,
  CustomerTenureColumn,
  FreeUserColumn
} from './UserRowColumns';

const UserRow = ({ 
  user, 
  getCancellationBadgeInfo,
  isNewCustomer,
  getEnterpriseNameForUser,
  getEnterpriseMetaForUser,
  getSubscriptionColor,
  formatDate,
  formatExactDateTime,
  timeAgo,
  getTrialEndDate,
  formatSubscriptionEndDate,
  onSyncSubscription,
  onCancelSubscription,
  onDeleteUser,
  showTrialEnd = false,
  showSubscriptionInfo = true,
  showCancellationDate = false,
  showDeletionDate = false,
  showNextDecisionDate = false,
  showFreeUser = true,
  showActions = true,
  isEnterpriseSection = false
}) => {
  const cancellationInfo = getCancellationBadgeInfo(user);
  const displayName = getUserDisplayName(user);
  const decisionInfo = showNextDecisionDate ? getNextDecisionDate(user, formatDate) : null;
  
  // Debug logging for name display issues
  if (displayName === 'No Name') {
    console.log('UserRow: User with missing name data:', {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      firstName: user.firstName,
      lastName: user.lastName,
      resolvedName: displayName
    });
  }
  
  return (
    <tr key={user.uid} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
      {/* Name Column */}
      <NameColumn 
        user={user}
        displayName={displayName}
        cancellationInfo={cancellationInfo}
        isNewCustomer={isNewCustomer}
        isEnterpriseSection={isEnterpriseSection}
        getEnterpriseMetaForUser={getEnterpriseMetaForUser}
      />

      {/* Enterprise Column - EARLY POSITION (only for enterprise section) */}
      {isEnterpriseSection && (
        <EnterpriseColumn 
          user={user}
          getEnterpriseNameForUser={getEnterpriseNameForUser}
          getEnterpriseMetaForUser={getEnterpriseMetaForUser}
        />
      )}

      {/* Email Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">{user.email}</div>
      </td>

      {/* Phone Column */}
      <PhoneColumn phone={user.phone} />

      {/* Role Column */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        {user.role ? user.role : <span className="text-gray-400">-</span>}
      </td>

      {/* Time Until End Column (conditional) */}
      {showTrialEnd && (
        <TrialEndColumn user={user} getTrialEndDate={getTrialEndDate} />
      )}

      {/* Next Decision Date Column */}
      {showNextDecisionDate && (
        <NextDecisionDateColumn decisionInfo={decisionInfo} />
      )}

      {/* Member Since Column */}
      <MemberSinceColumn 
        user={user}
        formatDate={formatDate}
        formatExactDateTime={formatExactDateTime}
        timeAgo={timeAgo}
      />

      {/* Last Active Column */}
      <LastActiveColumn lastActive={user.lastActive} />

      {/* Subscription Column (conditional) */}
      {showSubscriptionInfo && (
        <SubscriptionColumn user={user} />
      )}

      {/* Date Cancelled Column (conditional) */}
      {showCancellationDate && (
        <CancellationDateColumn user={user} formatDate={formatDate} />
      )}

      {/* Deletion Date Column (conditional) */}
      {showDeletionDate && (
        <DeletionDateColumn user={user} formatDate={formatDate} />
      )}

      {/* Activity Metrics */}
      <MetricBadgeColumn value={user.totalTemplatePageHits} colorClass="bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400" />
      <MetricBadgeColumn value={user.totalTemplatesEdited} colorClass="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" />
      <MetricBadgeColumn value={user.totalVideoTemplatesEdited} colorClass="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" />
      <MetricBadgeColumn value={user.campaignsCreated} colorClass="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" />
      <MetricBadgeColumn value={user.liveSupportClicks} colorClass="bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400" />
      <MetricBadgeColumn value={user.bookCalendlyClicks} colorClass="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400" />
      <MetricBadgeColumn value={user.visitRobocentClicks} colorClass="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" />
      <MetricBadgeColumn value={user.totalWebsitePageViews} colorClass="bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400" />
      <MetricBadgeColumn value={user.totalWebsitesVisited} colorClass="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" />
      <MetricBadgeColumn value={user.totalWebsiteCalendlyClicks} colorClass="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400" />
      <MetricBadgeColumn value={user.surveyVideosCollected} colorClass="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" />
      <MetricBadgeColumn value={user.totalStockVideosDownloaded} colorClass="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" />
      <MetricBadgeColumn value={user.namespacesCount} colorClass="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400" />

      {/* Engagement Column */}
      <EngagementColumn currentStreak={user.currentStreak} activeDaysThisMonth={user.activeDaysThisMonth} />

      {/* Revenue Columns */}
      <RevenueColumn amount={user.monthlyPrice} isMonthly />
      <RevenueColumn amount={user.totalRevenue} />
      <CustomerTenureColumn months={user.monthsAsPayingCustomer} />

      {/* Enterprise Column - LATE POSITION (only for non-enterprise sections) */}
      {!isEnterpriseSection && (
        <EnterpriseColumn 
          user={user}
          getEnterpriseNameForUser={getEnterpriseNameForUser}
          getEnterpriseMetaForUser={getEnterpriseMetaForUser}
        />
      )}

      {/* Free User Column (conditional) */}
      {showFreeUser && (
        <FreeUserColumn isFreeUser={user.isFreeUser} />
      )}

      {/* Actions Column (conditional) */}
      {showActions && (
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onDeleteUser && onDeleteUser(user)}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              title="Delete user account and all associated data"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </button>
            <button
              onClick={() => onCancelSubscription && onCancelSubscription(user)}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
              title="Cancel subscription and downgrade to basic"
            >
              <CreditCard className="w-3 h-3 mr-1" />
              Cancel Subscription
            </button>
            <button
              onClick={() => onSyncSubscription(user)}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              title="Sync subscription data with Stripe"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Sync Subscription
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

// Cancellation Date Column Component
function CancellationDateColumn({ user, formatDate }) {
  // Get the most relevant cancellation date for this user
  let cancelDate = null;
  let cancelType = '';
  
  // For former PAYING subscribers, prioritize subscription end dates
  if (user.subscriptionPeriodEnd) {
    const periodEndDate = user.subscriptionPeriodEnd?.toDate ? user.subscriptionPeriodEnd.toDate() : new Date(user.subscriptionPeriodEnd);
    const now = new Date();
    if (periodEndDate < now) {
      cancelDate = periodEndDate;
      cancelType = 'Subscription ended';
    }
  }
  
  if (!cancelDate && user.subscriptionCancelAt) {
    const cancelAtDate = user.subscriptionCancelAt?.toDate ? user.subscriptionCancelAt.toDate() : new Date(user.subscriptionCancelAt);
    const now = new Date();
    if (cancelAtDate < now) {
      cancelDate = cancelAtDate;
      cancelType = 'Cancelled';
    }
  }
  
  if (!cancelDate && (user.totalRevenue > 0 || user.firstPaymentDate)) {
    if (user.createdAt) {
      const createdDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      const estimatedEndDate = new Date(createdDate.getTime() + (60 * 24 * 60 * 60 * 1000));
      const now = new Date();
      if (estimatedEndDate < now) {
        cancelDate = estimatedEndDate;
        cancelType = 'Estimated end';
      }
    }
  }
  
  if (!cancelDate && !(user.totalRevenue > 0 || user.firstPaymentDate) && user.lastActive) {
    cancelDate = user.lastActive?.toDate ? user.lastActive.toDate() : new Date(user.lastActive);
    cancelType = 'Last seen';
  }
  
  if (!cancelDate) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <span className="text-gray-400">N/A</span>
      </td>
    );
  }
  
  const now = new Date();
  const timeDiff = now.getTime() - cancelDate.getTime();
  
  if (timeDiff <= 0) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <div className="flex flex-col">
          <span className="text-red-500 text-xs">Future date?</span>
          <span className="text-xs text-gray-500">{formatDate(cancelDate)}</span>
        </div>
      </td>
    );
  }
  
  const { timeText, colorClass } = getTimeAgoInfo(timeDiff);
  
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      <div className="flex flex-col">
        <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${colorClass}`}>
          {timeText}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {cancelType} • {formatDate(cancelDate)}
        </span>
      </div>
    </td>
  );
}

// Deletion Date Column Component
function DeletionDateColumn({ user, formatDate }) {
  let deleteDate = null;
  let deleteType = '';
  
  if (user.deletedAt) {
    deleteDate = user.deletedAt?.toDate ? user.deletedAt.toDate() : new Date(user.deletedAt);
    deleteType = 'Deleted';
  } else if (user.lastActive) {
    deleteDate = user.lastActive?.toDate ? user.lastActive.toDate() : new Date(user.lastActive);
    deleteType = 'Last seen';
  } else if (user.createdAt) {
    deleteDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
    deleteType = 'Created';
  }
  
  if (!deleteDate) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <span className="text-gray-400">N/A</span>
      </td>
    );
  }
  
  const now = new Date();
  const timeDiff = now.getTime() - deleteDate.getTime();
  const isPast = timeDiff > 0;
  
  let timeText = '';
  let colorClass = '';
  
  if (isPast) {
    const { timeText: txt, colorClass: cls } = getTimeAgoInfo(timeDiff);
    timeText = txt;
    colorClass = cls;
  } else {
    const daysUntil = Math.ceil(-timeDiff / (1000 * 60 * 60 * 24));
    timeText = `in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`;
    colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
  }
  
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      <div className="flex flex-col">
        <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${colorClass}`}>
          {timeText}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {deleteType} • {formatDate(deleteDate)}
        </span>
      </div>
    </td>
  );
}

// Helper function for time ago formatting
function getTimeAgoInfo(timeDiff) {
  const minutes = Math.floor(timeDiff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  
  let timeText = '';
  if (months > 0) {
    timeText = `${months} month${months !== 1 ? 's' : ''} ago`;
  } else if (weeks > 0) {
    timeText = `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  } else if (days > 0) {
    timeText = `${days} day${days !== 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    timeText = `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (minutes > 0) {
    timeText = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else {
    timeText = 'Just now';
  }
  
  let colorClass = '';
  if (days <= 1) {
    colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  } else if (days <= 7) {
    colorClass = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
  } else if (days <= 30) {
    colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  } else {
    colorClass = 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  }
  
  return { timeText, colorClass };
}

export default UserRow;
